import { lazy, useState, Suspense } from 'react';

import { useAppSelector } from 'store';
import { formatDate } from 'utils/date';
import { Badge } from 'components/badge';
import { hasUserRole } from 'store/slices/auth';
import { BadgeColor, BadgePadding } from 'types';
import {
  EventCheckout,
  RepChangeHistoryEntry,
  useRepChangeHistoryQuery,
} from 'store/api/event';

import * as Styles from './styles';
import {
  formatSplit,
  changeRepsRoles,
  getBlockedReason,
  formatSignedMoney,
} from './helpers';

// Loaded when opened: the dialog reuses the checkout rep picker, which
// imports the components index this file is part of.
const ChangeRepsDialog = lazy(() =>
  import('./dialog').then((module) => ({ default: module.ChangeRepsDialog }))
);

/**
 * "Change reps" beside Sales Reps on a closed receipt (owner go 24 Oct
 * 2026): Company Owner and Administrative only; disabled with the reason on
 * a sale this first version blocks.
 */
export const ChangeRepsControl = ({
  checkout,
}: {
  checkout: EventCheckout;
}) => {
  const allowed = useAppSelector(hasUserRole(changeRepsRoles));
  const [opened, setOpened] = useState(false);

  if (!allowed || !checkout?.id) {
    return null;
  }
  const blockedReason = getBlockedReason(checkout);

  return (
    <>
      <Styles.LinkButton
        type="button"
        title={blockedReason}
        data-testid="change-reps"
        disabled={!!blockedReason}
        onClick={() => setOpened(true)}
      >
        Change reps
      </Styles.LinkButton>
      {blockedReason && <Styles.Note>{blockedReason}</Styles.Note>}
      {opened && (
        <Suspense fallback={null}>
          <ChangeRepsDialog
            checkout={checkout}
            onClose={() => setOpened(false)}
          />
        </Suspense>
      )}
    </>
  );
};

/** The rep changes of a receipt, newest first ([] while loading). */
export const useRepChangeHistory = (checkoutId?: number) =>
  useRepChangeHistoryQuery(Number(checkoutId), { skip: !checkoutId }).data ||
  [];

export const EditedBadge = () => (
  <Badge
    text="Edited"
    padding={BadgePadding.Small}
    styleType={BadgeColor.TetrieryBlue}
  />
);

const HistoryEntry = ({ entry }: { entry: RepChangeHistoryEntry }) => (
  <Styles.HistoryEntry>
    <Styles.Note>
      {formatDate(entry.createdAt, 'L LT')} · {entry.actorName || 'Unknown'}
      {entry.actorRole ? ` (${entry.actorRole})` : ''}
    </Styles.Note>
    <p>
      Reps: {formatSplit(entry.from)} → {formatSplit(entry.to)}
    </p>
    <p>Reason: {entry.reason}</p>
    {entry.pay && (
      <Styles.Note>
        Pay:{' '}
        {entry.pay
          .filter((rep) => rep.total !== 0)
          .map((rep) => `${rep.name} ${formatSignedMoney(rep.total)}`)
          .join(' · ') || 'no change'}{' '}
        · receipt rebuilt
      </Styles.Note>
    )}
  </Styles.HistoryEntry>
);

/** The History block of a receipt whose reps were changed. */
export const RepChangeHistory = ({
  entries,
}: {
  entries: RepChangeHistoryEntry[];
}) =>
  entries.length ? (
    <Styles.History data-testid="rep-change-history">
      <b>History</b>
      {entries.map((entry) => (
        <HistoryEntry entry={entry} key={entry.id} />
      ))}
    </Styles.History>
  ) : null;
